import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Truck, Plus, Loader2, Pencil, Trash2, FileText, Ban, Printer, RefreshCw } from "lucide-react";
import { GlsShipmentDialog, openPdf } from "@/components/GlsLabelDialog";

const COD_FEE = 9;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const fmtDate = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "");

const SelectAll = ({ rows, selected, setSelected, testId }) => (
  <Checkbox
    checked={rows.length > 0 && selected.length === rows.length}
    onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.id) : [])}
    data-testid={testId}
  />
);

const Row = ({ s, checked, onCheck, children }) => (
  <div className="flex flex-wrap items-start gap-3 px-4 py-3 border-b last:border-0 hover:bg-gray-50 transition-colors" data-testid={`gls-row-${s.id}`}>
    <Checkbox checked={checked} onCheckedChange={onCheck} className="mt-1" data-testid={`gls-check-${s.id}`} />
    <div className="flex-1 min-w-[200px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-900">{s.name || "—"}</span>
        {s.woo_order_id && <span className="text-xs text-blue-700 bg-blue-50 px-1.5 rounded">#{s.woo_order_id}</span>}
        {!s.order_id && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 rounded">Manuale</span>}
        {Number(s.codValue) > 0 && <span className="text-xs text-orange-700 bg-orange-50 px-1.5 rounded">Contrassegno €{(Number(s.codValue) + COD_FEE).toFixed(2)}</span>}
        {s.exchange && <span className="text-xs text-purple-700 bg-purple-50 px-1.5 rounded">Exchange</span>}
      </div>
      <div className="text-sm text-gray-500 break-words">{s.street1}, {s.postalCode} {s.city} {s.state} · {s.weight} kg</div>
      <div className="text-xs text-gray-500 flex flex-wrap gap-x-3" data-testid={`gls-contacts-${s.id}`}>
        <span>Tel: {s.phone || <span className="text-orange-600">mancante</span>}</span>
        <span className="truncate">Email: {s.email || <span className="text-gray-400">—</span>}</span>
      </div>
      {s.cap_warning && <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5 mt-1 inline-block" data-testid={`gls-cap-warning-${s.id}`}>{s.cap_warning}</div>}
      {s.tracking && <div className="text-xs text-gray-500">Tracking <b className="text-gray-800">{s.tracking}</b> · {fmtDate(s.generated_at)}{s.cost != null && ` · €${s.cost}`}</div>}
    </div>
    <div className="flex items-center gap-1 shrink-0 ml-auto">{children}</div>
  </div>
);

export default function GlsLabels() {
  const [tab, setTab] = useState("queue");
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/gls/shipments?view=${tab}`);
      setRows(r.data);
    } catch {
      toast.error("Errore caricamento etichette");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { setSelected([]); load(); }, [load]);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const generate = async (ids) => {
    const done = [];
    for (let i = 0; i < ids.length; i++) {
      setBusy(`Genero ${i + 1} di ${ids.length}…`);
      try {
        await api.post(`/gls/shipments/${ids[i]}/generate`, {}, { timeout: 90000 });
        done.push(ids[i]);
      } catch (e) {
        const name = rows.find((r) => r.id === ids[i])?.name;
        toast.error(`${name}: ${e.response?.data?.detail || "errore"}`);
      }
      if (i < ids.length - 1) await sleep(2100);
    }
    setBusy(null);
    setSelected([]);
    if (done.length) {
      toast.success(`${done.length} etichette generate`);
      openPdf("/gls/labels/merged", { ids: done });
    }
    load();
  };

  const cancel = async (s) => {
    if (!window.confirm(`Annullare l'etichetta ${s.tracking}? Il credito verrà rimborsato e l'ordine tornerà in coda.`)) return;
    setBusy("Annullo…");
    try {
      const r = await api.post(`/gls/shipments/${s.id}/cancel`);
      toast.success(`Etichetta annullata · rimborso €${r.data.refunded ?? "-"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore annullamento");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s) => {
    await api.delete(`/gls/shipments/${s.id}`);
    toast.success("Rimossa dalla coda");
    load();
  };

  const queue = tab === "queue";
  const created = rows.filter((r) => r.status === "created");
  const selectable = queue ? rows : created;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="gls-labels-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif text-gray-900 flex items-center gap-3"><Truck className="w-8 h-8 text-yellow-600" />Etichette GLS</h1>
          <p className="text-gray-500 text-sm mt-1">Crea, stampa e annulla le etichette senza aprire Spedisci.online</p>
        </div>
        <Button onClick={() => setEditing({})} variant="outline" data-testid="gls-new-manual-btn"><Plus className="w-4 h-4 mr-1" />Etichetta manuale</Button>
      </div>

      <div className="flex gap-2 border-b">
        {[["queue", "Da generare"], ["history", "Generate (30 giorni)"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`gls-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-[#C9A227] text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
            {l}{tab === k && !loading && <span className="ml-2 text-xs text-gray-400">{rows.length}</span>}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border shadow-sm">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b bg-gray-50 rounded-t-xl">
          <SelectAll rows={selectable} selected={selected} setSelected={setSelected} testId="gls-select-all" />
          <span className="text-sm text-gray-600">{selected.length ? `${selected.length} selezionate` : "Seleziona tutte"}</span>
          <div className="flex-1" />
          {busy && <span className="text-sm text-gray-600 flex items-center gap-1" data-testid="gls-busy"><Loader2 className="w-4 h-4 animate-spin" />{busy}</span>}
          <Button size="sm" variant="ghost" onClick={load} data-testid="gls-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
          {queue ? (
            <Button size="sm" disabled={!selected.length || !!busy} onClick={() => generate(selected)} className="bg-[#C9A227] hover:bg-[#b08d1f] text-white" data-testid="gls-generate-selected-btn">
              <Truck className="w-4 h-4 mr-1" />Genera selezionate
            </Button>
          ) : (
            <Button size="sm" disabled={!selected.length} onClick={() => openPdf("/gls/labels/merged", { ids: selected })} data-testid="gls-print-selected-btn">
              <Printer className="w-4 h-4 mr-1" />PDF selezionate
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-gray-500 text-sm" data-testid="gls-empty">
            {queue ? "Nessuna etichetta in coda. Seleziona gli ordini in Archivio Vendite e premi \"Spedizione GLS\"." : "Nessuna etichetta negli ultimi 30 giorni."}
          </div>
        ) : rows.map((s) => (
          <Row key={s.id} s={s} checked={selected.includes(s.id)} onCheck={() => toggle(s.id)}>
            {queue ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => setEditing(s)} data-testid={`gls-edit-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`gls-remove-${s.id}`}><Trash2 className="w-4 h-4 text-gray-400" /></Button>
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => generate([s.id])} data-testid={`gls-generate-${s.id}`}>Genera</Button>
              </>
            ) : s.status === "created" ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => openPdf(`/gls/shipments/${s.id}/label`)} data-testid={`gls-pdf-${s.id}`}><FileText className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => cancel(s)} data-testid={`gls-cancel-${s.id}`}><Ban className="w-4 h-4 text-red-500" /></Button>
              </>
            ) : (
              <Badge variant="outline" className="text-gray-500" data-testid={`gls-cancelled-${s.id}`}>Annullata</Badge>
            )}
          </Row>
        ))}
      </div>

      <GlsShipmentDialog shipment={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
